import { Component, OnInit, Inject, ViewChild, inject, signal } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyInput as MatInput } from '@angular/material/legacy-input';
import { SearchModel } from 'src/app/models/search-model';
import { RecordResult } from 'src/app/models/record-result';
import { ApiService } from 'src/app/services/api.service';
import { Filter } from 'src/app/models/filter';
import { FilterValue } from 'src/app/models/filter-value';
import { Util } from 'src/app/util';
import { SearchTerm } from 'src/app/state/user/searches/models/search';
import { Icon } from 'src/app/models/record';
import { UserService } from 'src/app/services/user.service';
import { PurchaseSuggestion } from 'src/app/models/purchase-suggestion';
import { ResizeService } from 'src/app/services/resize.service';
import { Router } from '@angular/router';
import { SolutionType } from 'src/app/models/client-configuration';
import { ConfigService } from 'src/app/services/config.service';
import { LocaleService } from 'src/app/services/locale.service';
import { ToastService } from 'src/app/_core/services/toast.service';

@Component({
  selector: 'app-purchase-suggestions-dialog',
  templateUrl: './purchase-suggestions-dialog.component.html',
  styleUrls: ['./purchase-suggestions-dialog.component.scss'],
})
export class PurchaseSuggestionsDialogComponent implements OnInit {
  private readonly toastService = inject(ToastService);

  readonly showDeleteSuggestionDialog = signal<boolean>(false);
  readonly suggestionItemToDelete = signal<PurchaseSuggestion>(null);

  @ViewChild('query', { read: MatInput, static: false })
  queryRef: MatInput;

  solutionType: string;
  searchModel: SearchModel;
  result: RecordResult;
  filters: Filter[];
  formatFilter: Filter;
  open: boolean;
  hasLoadMore: boolean;
  waiting: boolean;
  searching: boolean;
  cancelledRecordId: string;
  textDirection: string;

  constructor(
    public userService: UserService,
    private router: Router,
    private resizeService: ResizeService,
    private apiService: ApiService,
    private localeService: LocaleService,
    configService: ConfigService,
    public dialogRef: MatDialogRef<PurchaseSuggestionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SearchModel
  ) {
    this.searchModel = data;
    this.search(false);
    this.solutionType = configService.solutionType;
  }

  ngOnInit() {
    this.textDirection = this.localeService.getTextDirection();
    this.userService.loadPurchaseSuggestions();
  }

  addTerm(query: string) {
    this.searchModel.addTerm({ field: Util.ALLFIELDS, term: query });
    this.queryRef.value = '';
    this.search();
  }

  removeTerm(term: SearchTerm) {
    this.searchModel.removeTerm(term);
    this.search();
  }

  applyFilter(name: string, value: string) {
    this.searchModel.applyFilter({ name: name, value: value });
    this.search();

    if (this.resizeService.isMobile) {
      this.open = false;
    }

    const dialogHeader = document.querySelector('.dialog__header');
    dialogHeader.scrollIntoView({ behavior: 'smooth' });
  }

  getExpandedValues(values: FilterValue[]): FilterValue[] {
    return values.slice(0, Util.MAX_FILTER_VALUES);
  }

  getCollapsedValues(values: FilterValue[]): FilterValue[] {
    return values.slice(Util.MAX_FILTER_VALUES, Util.MAX_EXPANDED_VALUES);
  }

  hasViewMore(values: FilterValue[]): boolean {
    return values.length > Util.MAX_FILTER_VALUES;
  }

  toggleValuesPanel(filter: Filter) {
    filter.expanded = !filter.expanded;
    filter.action = filter.expanded ? 'common.viewLess' : 'common.viewMore';
    filter.icon = filter.expanded ? 'remove' : 'add';
  }

  adapt(filter: Filter, value: FilterValue): string {
    if (Util.LANGUAGE_FILTERS.includes(filter.name) && value.name.length === 3) {
      return `languages.${value.name}`;
    }

    return value.name;
  }

  getFormatInfo(format: string): Icon {
    return Util.resolveIcon(format);
  }

  loadMore() {
    this.waiting = true;
    this.searchModel.offset += this.searchModel.limit;
    this.search(true);
  }

  handleSuggestionToDelete(suggestion: PurchaseSuggestion) {
    this.showDeleteSuggestionDialog.set(true);
    this.suggestionItemToDelete.set(suggestion);
  }

  deleteSuggestion() {
    this.showDeleteSuggestionDialog.set(false);
    this.apiService.cancelUserPurchaseSuggestions(this.suggestionItemToDelete()).subscribe((result) => {
      this.cancelledRecordId = (result as PurchaseSuggestion).nInf;
      this.userService.purchaseSuggestions = this.userService.purchaseSuggestions.filter((suggestion) => suggestion.id !== this.suggestionItemToDelete().id);
      this.toastService.showToast('messages.suggestions.removed');
    });
  }

  goToUserSuggestions() {
    this.router.navigate(['/user/suggestions'], {}).then(() => this.dialogRef.close());
  }

  private search(loadMore?: boolean) {
    this.searching = !loadMore && true;

    this.apiService.getPurchaseSuggestionsBySearch(this.searchModel.toRequest()).subscribe((results) => {
      this.result = this.parseResults(results, loadMore);
      if (results.recordIds.length) {
        this.getRecordSuggestions();
        this.checkIfInLibrary();
        this.parseFilters();
        this.markSelectedFilters();
      }
      this.waiting = false;
      this.searching = false;
    });
  }

  private parseResults(results: RecordResult, loadMore?: boolean): RecordResult {
    if (results.facets) {
      results.facets.forEach((f) => {
        f.icon = 'add';
        f.action = 'common.viewMore';
      });

      this.hasLoadMore = this.searchModel.offset + results.recordIds.length < results.total;
    }

    if (loadMore) {
      results.recordIds.unshift(...this.result.recordIds);
      results.records.unshift(...this.result.records);
    }

    return results;
  }

  private parseFilters() {
    if (this.result.facets) {
      this.filters = this.result.facets.filter((f) => f.name !== Util.OPL_FORMAT_FILTER);
      this.formatFilter = this.result.facets.find((f) => f.name === Util.OPL_FORMAT_FILTER);
    }
  }

  private getRecordSuggestions() {
    this.apiService.getPurchaseSuggestions(this.result.recordIds).subscribe((suggestions) =>
      Object.keys(suggestions).forEach((s) => {
        let n = suggestions[s];

        if (this.userService.purchaseSuggestions.some((p) => p.nInf === s)) {
          n -= 1;
        }

        this.result.records.find((r) => r.id === s).requestedBy = n;
      })
    );
  }

  private checkIfInLibrary() {
    const recordIsbn = this.result.records.filter((r) => r.isbn).map((r) => r.isbn);

    this.apiService
      .checkIfExistsInLibrary(recordIsbn)
      .subscribe((checkResult) =>
        Object.keys(checkResult).forEach(
          (k) => (this.result.records.find((r) => r.isbn === k).inLibrary = checkResult[k])
        )
      );
  }

  private markSelectedFilters() {
    this.searchModel.filters.forEach((filter) => {
      // TODO: fix in OTK!
      if (!this.result.facets) {
        return;
      }

      this.result.facets.forEach((f) => {
        f.values.forEach((v) => {
          if (f.name === filter.name && v.name === filter.value) {
            v.selected = true;
          }
        });
      });
    });
  }
}
